// src/components/gestionEvento/ConvocatoriaForm.js - Versión Mejorada

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Fade,
  Stack,
  Divider,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  Clear as ClearIcon,
  Html as HtmlIcon,
  PictureAsPdf as PdfIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import jsPDF from 'jspdf';
import DOMPurify from 'dompurify';
import { storage } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import SigertreLoader from '../../Common/SigertreLoader';

const MAX_PDF_SIZE = 5 * 1024 * 1024; // 5MB (aumentado)
const CONVOCATORIA_FILENAME = 'convocatoria.pdf';
const ALLOWED_FORMATS = ['application/pdf'];

// Componente para el área de subida de archivos mejorada
const FileUploadArea = ({ onFileSelect, loading, hasFile, fileName, onClear }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect({ target: { files: e.dataTransfer.files } });
    }
  }, [onFileSelect]);

  return (
    <Card 
      sx={{ 
        border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
        }
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <CardContent sx={{ textAlign: 'center', py: 4 }}>
        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="convocatoria-pdf-upload"
          type="file"
          onChange={onFileSelect}
          disabled={loading}
        />
        
        {hasFile ? (
          <Stack spacing={2} alignItems="center">
            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="success.main">
              Archivo seleccionado
            </Typography>
            <Chip 
              label={fileName} 
              color="success" 
              variant="outlined"
              onDelete={onClear}
              deleteIcon={<ClearIcon />}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              component="label"
              htmlFor="convocatoria-pdf-upload"
              size="small"
            >
              Cambiar archivo
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Arrastra tu PDF aquí o haz clic para seleccionar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Máximo {MAX_PDF_SIZE / 1024 / 1024}MB
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              component="label"
              htmlFor="convocatoria-pdf-upload"
              disabled={loading}
            >
              Seleccionar PDF
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// Componente para la vista previa del PDF
const PDFPreview = ({ url, title = "Vista previa PDF", height = 500 }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ p: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, px: 1 }}>
          {title}
        </Typography>
        <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden' }}>
          {loading && (
            <Box 
              sx={{ 
                position: 'absolute', 
                inset: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.1)',
                zIndex: 1 
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {error ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                flexDirection: 'column',
                gap: 2 
              }}
            >
              <PdfIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography color="text.secondary">
                Error al cargar el PDF
              </Typography>
            </Box>
          ) : (
            <iframe
              src={url}
              width="100%"
              height="100%"
              title={title}
              style={{ border: 'none' }}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError(true);
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ConvocatoriaForm = ({ competencia, updateConvocatoria }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Estados principales
  const [isEditing, setIsEditing] = useState(false);
  const [editMode, setEditMode] = useState('text');
  const [displayPreference, setDisplayPreference] = useState('auto'); // 'auto', 'text', 'pdf'
  const [content, setContent] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const [restoredDraft, setRestoredDraft] = useState(false);
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDialog, setOpenDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Configuración Quill mejorada (sin imágenes, videos y código)
  const toolbarOptions = [
    [{ 'font': [] }, { 'size': [] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link'],
    ['blockquote'],
    ['clean']
  ];

  const modules = { 
    toolbar: toolbarOptions,
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'font', 'size', 'header', 'bold', 'italic', 'underline', 'strike', 
    'color', 'background', 'script', 'list', 'bullet', 'indent', 'align', 
    'link', 'blockquote'
  ];

  // Inicialización desde props
  useEffect(() => {
    if (competencia?.id) {
      const convocatoriaData = competencia.convocatoria || {};
      const initialContent = convocatoriaData.convocatoria || '';
      const initialPdfUrl = convocatoriaData.convocatoriaPDF || '';
      const initialDisplayPreference = convocatoriaData.displayPreference || 'auto';

      setContent(initialContent);
      setCurrentPdfUrl(initialPdfUrl);
      setPreviewPdfUrl(initialPdfUrl);
      setDisplayPreference(initialDisplayPreference);

      // Determinar modo de edición inicial
      const startEditing = !initialContent && !initialPdfUrl;
      setIsEditing(startEditing);
      
      // Si hay ambos, usar la preferencia guardada, sino usar lo que esté disponible
      if (initialContent && initialPdfUrl) {
        setEditMode(initialDisplayPreference === 'pdf' ? 'pdf' : 'text');
      } else if (initialPdfUrl) {
        setEditMode('pdf');
      } else {
        setEditMode('text');
      }
      
      setHasUnsavedChanges(false);
    } else {
      setContent('');
      setCurrentPdfUrl('');
      setPreviewPdfUrl('');
      setDisplayPreference('auto');
      setIsEditing(true);
      setEditMode('text');
    }
  }, [competencia]);

  // Restaurar borrador si no hay contenido y existe en localStorage
  useEffect(() => {
    if (!competencia?.id) return;
    if (content?.trim()) return; // ya hay contenido
    try {
      const raw = localStorage.getItem(`convocatoria_draft_${competencia.id}`);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.content && typeof draft.content === 'string') {
        setContent(draft.content);
        setDisplayPreference(draft.displayPreference || 'auto');
        setRestoredDraft(true);
        setSnackbar({ open: true, message: 'Borrador restaurado automáticamente.', severity: 'info' });
      }
    } catch {}
  }, [competencia?.id]);

  // Detectar cambios no guardados
  useEffect(() => {
    if (competencia?.convocatoria) {
      const originalContent = competencia.convocatoria.convocatoria || '';
      const originalPdfUrl = competencia.convocatoria.convocatoriaPDF || '';
      const originalDisplayPreference = competencia.convocatoria.displayPreference || 'auto';
      
      const hasContentChanged = content !== originalContent;
      const hasPdfChanged = Boolean(pdfFile) || (currentPdfUrl !== originalPdfUrl);
      const hasDisplayPreferenceChanged = displayPreference !== originalDisplayPreference;
      
      setHasUnsavedChanges(hasContentChanged || hasPdfChanged || hasDisplayPreferenceChanged);
    }
  }, [content, pdfFile, currentPdfUrl, displayPreference, competencia]);

  // Autosave (solo texto y preferencia) cada 2s de inactividad
  useEffect(() => {
    if (!competencia?.id) return;
    const handle = setTimeout(() => {
      try {
        const payload = JSON.stringify({ content, displayPreference, ts: Date.now() });
        localStorage.setItem(`convocatoria_draft_${competencia.id}`, payload);
        setLastAutoSavedAt(new Date());
      } catch {}
    }, 2000);
    return () => clearTimeout(handle);
  }, [content, displayPreference, competencia?.id]);

  // Función de subida de PDF con progreso
  const handleUploadPDF = useCallback(async (file) => {
    if (!competencia?.id) throw new Error("ID de competencia no disponible.");
    if (!file) throw new Error('No se proporcionó archivo.');
    if (!ALLOWED_FORMATS.includes(file.type)) throw new Error('Solo se permiten archivos PDF.');
    if (file.size > MAX_PDF_SIZE) throw new Error(`Archivo muy grande (máx. ${MAX_PDF_SIZE / 1024 / 1024}MB).`);

    setLoading(true);
    setUploadProgress(0);
    
    try {
      const storageRef = ref(storage, `competencias/${competencia.id}/${CONVOCATORIA_FILENAME}`);
      
      // Simular progreso (Firebase no proporciona progreso real para uploadBytes)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      return url;
    } catch (error) {
      console.error("Error subiendo PDF:", error);
      throw error;
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, [competencia]);

  // Función de borrado de PDF
  const handleDeletePDFStorage = useCallback(async () => {
    if (!competencia?.id) throw new Error("ID no disponible.");
    
    setLoading(true);
    try {
      const storageRef = ref(storage, `competencias/${competencia.id}/${CONVOCATORIA_FILENAME}`);
      await deleteObject(storageRef);
      return true;
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        return true; // OK si no estaba
      }
      console.error("Error eliminando PDF:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [competencia]);

  // Guardar cambios
  const handleSave = async () => {
    if (!competencia?.id || typeof updateConvocatoria !== 'function') {
      setSnackbar({ open: true, message: 'Error: Datos no disponibles para guardar.', severity: 'error' });
      return;
    }

    setLoading(true);
    let finalContent = DOMPurify.sanitize(content);
    let pdfUrlToSave = currentPdfUrl;

    try {
      if (pdfFile) {
        const newPdfUrl = await handleUploadPDF(pdfFile);
        pdfUrlToSave = newPdfUrl;
        setPdfFile(null);
      }

      const convocatoriaDataToSave = {
        convocatoria: finalContent,
        convocatoriaPDF: pdfUrlToSave,
        displayPreference: displayPreference,
      };

      const result = await updateConvocatoria(competencia.id, convocatoriaDataToSave);

      if (result?.success) {
        setContent(finalContent);
        setCurrentPdfUrl(pdfUrlToSave);
        setPreviewPdfUrl(pdfUrlToSave);
        setIsEditing(false);
        setHasUnsavedChanges(false);
        setSnackbar({ open: true, message: '✅ Convocatoria guardada exitosamente.', severity: 'success' });
      } else {
        throw result?.error || new Error("Error desconocido al guardar.");
      }
    } catch (error) {
      console.error('Error en handleSave:', error);
      setSnackbar({ open: true, message: `❌ Error al guardar: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Manejo de archivos mejorado
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    const inputElement = event.target;
    
    // Limpiar estado previo
    setPdfFile(null);
    setPreviewPdfUrl(currentPdfUrl);

    if (!file) {
      if (inputElement) inputElement.value = '';
      return;
    }

    // Validaciones
    if (!ALLOWED_FORMATS.includes(file.type)) {
      setSnackbar({ open: true, message: '⚠️ Formato inválido. Solo se permiten archivos PDF.', severity: 'warning' });
      if (inputElement) inputElement.value = '';
      return;
    }

    if (file.size > MAX_PDF_SIZE) {
      setSnackbar({ 
        open: true, 
        message: `⚠️ Archivo muy grande. Máximo ${MAX_PDF_SIZE / 1024 / 1024}MB.`, 
        severity: 'warning' 
      });
      if (inputElement) inputElement.value = '';
      return;
    }

    // Archivo válido
    setPdfFile(file);
    const blobUrl = URL.createObjectURL(file);
    setPreviewPdfUrl(blobUrl);
    setSnackbar({ open: true, message: '📄 Archivo PDF seleccionado correctamente.', severity: 'info' });
  };

  // Limpiar archivo seleccionado
  const handleClearFile = () => {
    setPdfFile(null);
    setPreviewPdfUrl(currentPdfUrl);
    const input = document.getElementById('convocatoria-pdf-upload');
    if (input) input.value = '';
  };

  // Funciones de descarga (sin cambios significativos)
  const handleDownloadHTML = () => {
    if (!content?.trim()) {
      setSnackbar({ open: true, message: '⚠️ No hay contenido de texto para descargar.', severity: 'warning' });
      return;
    }

    try {
      const cleanHtml = DOMPurify.sanitize(content);
      const element = document.createElement('a');
      const file = new Blob([cleanHtml], { type: 'text/html;charset=utf-8' });
      element.href = URL.createObjectURL(file);
      const filename = `convocatoria_${competencia?.general?.nombre || competencia.id}_${new Date().toISOString().split('T')[0]}.html`;
      element.download = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(element.href);
      setSnackbar({ open: true, message: '📄 HTML descargado exitosamente.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: '❌ Error descargando HTML.', severity: 'error' });
    }
  };

  const handleDownloadPDF = async () => {
    setLoading(true);
    try {
      if (currentPdfUrl) {
        const link = document.createElement('a');
        link.href = currentPdfUrl;
        const filename = `convocatoria_${competencia?.general?.nombre || competencia.id}_${new Date().toISOString().split('T')[0]}.pdf`;
        link.download = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({ open: true, message: '📄 Descarga de PDF iniciada.', severity: 'success' });
      } else if (content?.trim()) {
        const doc = new jsPDF('p', 'pt', 'a4');
        const tempDiv = document.createElement('div');
        tempDiv.style.cssText = `
          padding: 40pt;
          font-family: Helvetica, Arial, sans-serif;
          font-size: 10pt;
          line-height: 1.4;
          max-width: 515pt;
          word-wrap: break-word;
        `;
        tempDiv.innerHTML = DOMPurify.sanitize(content);
        document.body.appendChild(tempDiv);

        await doc.html(tempDiv, {
          callback: function (pdf) {
            const filename = `convocatoria_${competencia?.general?.nombre || competencia.id}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(filename.replace(/[^a-zA-Z0-9._-]/g, '_'));
            document.body.removeChild(tempDiv);
            setSnackbar({ open: true, message: '📄 PDF generado exitosamente.', severity: 'success' });
          },
          margin: [40, 40, 40, 40],
          autoPaging: 'text',
          width: 515,
          windowWidth: tempDiv.scrollWidth + 80,
          html2canvas: { scale: 0.75, useCORS: true, logging: false }
        });
      } else {
        setSnackbar({ open: true, message: '⚠️ No hay contenido para descargar.', severity: 'warning' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: `❌ Error al descargar: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Utilidades de conteo de palabras/caracteres
  const plainText = useMemo(() => {
    const tmp = document.createElement('div');
    tmp.innerHTML = DOMPurify.sanitize(content || '');
    const text = tmp.textContent || tmp.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  }, [content]);
  const wordCount = useMemo(() => (plainText ? plainText.split(' ').length : 0), [plainText]);
  const charCount = plainText.length;

  // Eliminar PDF
  const confirmRemovePDF = async () => {
    setOpenDialog(false);
    if (!currentPdfUrl) {
      setSnackbar({ open: true, message: 'ℹ️ No hay PDF guardado para eliminar.', severity: 'info' });
      return;
    }

    setLoading(true);
    try {
      await handleDeletePDFStorage();
      const result = await updateConvocatoria(competencia.id, { 
        convocatoria: content, 
        convocatoriaPDF: '',
        displayPreference: 'text' // Cambiar a texto cuando se elimina el PDF
      });

      if (result?.success) {
        setCurrentPdfUrl('');
        setPreviewPdfUrl('');
        setPdfFile(null);
        setDisplayPreference('text');
        setSnackbar({ open: true, message: '🗑️ PDF eliminado exitosamente.', severity: 'success' });
        setEditMode('text');
      } else {
        throw result?.error || new Error("Error actualizando base de datos.");
      }
    } catch (error) {
      setSnackbar({ open: true, message: `❌ Error: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Renderizado condicional del loader
  if (!competencia?.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <SigertreLoader
          loaderText="SIGERTRE"
          message="Cargando información de la competencia..."
          sx={{ '& .MuiTypography-root': { color: 'text.secondary', fontSize: '1.1rem', mt: 2 } }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Encabezado alineado con AjustesForm */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2, mb: 0.5 }}>
          Convocatoria
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestiona el contenido de la convocatoria en texto o PDF.
          {hasUnsavedChanges && (
            <Chip label="Cambios sin guardar" color="warning" size="small" sx={{ ml: 1 }} />
          )}
        </Typography>
      </Box>

      {/* Acción principal (Editar). Guardar se muestra junto a tabs */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
        {!isEditing && (
          <Tooltip title="Editar convocatoria">
            <span>
              <IconButton color="primary" onClick={() => setIsEditing(true)} disabled={loading} size="large">
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Sticky Tabs como en Ajustes */}
      {isEditing && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            pb: 1.5,
            pt: 0.5,
            background: `linear-gradient(180deg, ${theme.palette.background.default} 70%, ${alpha(
              theme.palette.background.default,
              0
            )} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Paper
              elevation={0}
              sx={{
                px: { xs: 0.75, sm: 1 },
                py: 0.5,
                borderRadius: 999,
                display: 'inline-flex',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(6px)',
              }}
            >
              <Tabs
                value={editMode}
                onChange={(_e, v) => setEditMode(v)}
                aria-label="Modo de edición"
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons={isMobile ? 'auto' : false}
                TabIndicatorProps={{
                  sx: {
                    height: '100%',
                    borderRadius: 999,
                    zIndex: -1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
                sx={{
                  minHeight: 44,
                  '& .MuiTabs-flexContainer': { gap: 0.5 },
                }}
              >
                <Tab
                  value="text"
                  label="Editar texto"
                  sx={{
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: { xs: 1.5, sm: 2.25 },
                    borderRadius: 999,
                    color: 'text.secondary',
                    '&.Mui-selected': { color: theme.palette.primary.main },
                  }}
                />
                <Tab
                  value="pdf"
                  label="Gestionar PDF"
                  sx={{
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: { xs: 1.5, sm: 2.25 },
                    borderRadius: 999,
                    color: 'text.secondary',
                    '&.Mui-selected': { color: theme.palette.primary.main },
                  }}
                />
              </Tabs>
            </Paper>

            <Tooltip title={hasUnsavedChanges ? 'Guardar cambios' : 'Sin cambios para guardar'}>
              <span>
                <IconButton color="primary" onClick={handleSave} disabled={loading || !hasUnsavedChanges} size="large">
                  {loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Contenedor principal similar a Ajustes/Pruebas */}
      <Box
        sx={{
          mt: 2,
          background: alpha(theme.palette.background.paper, 0.6),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          borderRadius: { xs: 0, md: 3 },
          boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          {/* Barra de progreso de subida */}
          {uploadProgress > 0 && (
            <Fade in={uploadProgress > 0}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Subiendo archivo... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            </Fade>
          )}

          {/* Opciones adicionales debajo de tabs */}
          {(currentPdfUrl || content?.trim() || isEditing) && (
            <Box sx={{ mb: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>

              {/* Selector de qué mostrar cuando hay ambos tipos de contenido - SOLO EN MODO EDICIÓN */}
              {isEditing && ((currentPdfUrl || pdfFile) && (content?.trim() || editMode === 'text')) && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Mostrar en la vista pública:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant={displayPreference === 'auto' ? 'contained' : 'outlined'}
                      startIcon={<PdfIcon />}
                      onClick={() => setDisplayPreference('auto')}
                      size="small"
                    >
                      Automático (Solo PDF)
                    </Button>
                    <Button
                      variant={displayPreference === 'text' ? 'contained' : 'outlined'}
                      startIcon={<TextFieldsIcon />}
                      onClick={() => setDisplayPreference('text')}
                      size="small"
                      disabled={!content?.trim()}
                    >
                      Mostrar texto
                    </Button>
                    <Button
                      variant={displayPreference === 'pdf' ? 'contained' : 'outlined'}
                      startIcon={<PdfIcon />}
                      onClick={() => setDisplayPreference('pdf')}
                      size="small"
                      disabled={!currentPdfUrl && !pdfFile}
                    >
                      Mostrar PDF y texto
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {displayPreference === 'auto' && 'Se mostrará solo el PDF si está disponible, sino el texto'}
                    {displayPreference === 'text' && 'Se mostrará el texto como principal (PDF disponible para descarga)'}
                    {displayPreference === 'pdf' && 'Se mostrará el PDF como principal con el texto como información adicional'}
                  </Typography>
                </Box>
              )}

              {/* Botón para eliminar PDF */}
              {isEditing && currentPdfUrl && (
                <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={() => setOpenDialog(true)}
                    disabled={loading}
                  >
                    Eliminar PDF guardado
                  </Button>
                </Box>
              )}
              </Box>
            </Box>
          )}

          {/* Área de edición */}
          {isEditing ? (
            <Card>
              <CardContent>
            {editMode === 'text' ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextFieldsIcon />
                  Editor de texto
                </Typography>
                <Box sx={{ 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 2,
                  '& .ql-editor': {
                    minHeight: '400px',
                    fontSize: '14px',
                    lineHeight: 1.6,
                  }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    placeholder="Escribe el contenido de la convocatoria aquí..."
                    modules={modules}
                    formats={formats}
                    readOnly={loading}
                  />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {wordCount} palabras • {charCount} caracteres
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lastAutoSavedAt ? `Borrador guardado ${lastAutoSavedAt.toLocaleTimeString()}` : restoredDraft ? 'Borrador restaurado' : 'Escribe para guardar borrador...'}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PdfIcon />
                  Gestión de archivo PDF
                </Typography>
                
                <FileUploadArea
                  onFileSelect={handleFileChange}
                  loading={loading}
                  hasFile={Boolean(pdfFile)}
                  fileName={pdfFile?.name}
                  onClear={handleClearFile}
                />

                {previewPdfUrl && (
                  <PDFPreview 
                    url={previewPdfUrl} 
                    title={pdfFile ? "Vista previa del archivo seleccionado" : "PDF actual guardado"}
                    height={600}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Área de visualización mejorada */
        <Card>
          <CardContent>
            {currentPdfUrl ? (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PdfIcon />
                    Convocatoria (PDF)
                  </Typography>
                  <Tooltip title="Descargar PDF">
                    <IconButton onClick={handleDownloadPDF} disabled={loading}>
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="600px">
                    <CircularProgress size={60} />
                  </Box>
                ) : (
                  <PDFPreview url={currentPdfUrl} title="Convocatoria" height={700} />
                )}
              </Box>
            ) : content?.trim() ? (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextFieldsIcon />
                    Convocatoria (Texto)
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Descargar como PDF">
                      <IconButton onClick={handleDownloadPDF} disabled={loading}>
                        <PdfIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar como HTML">
                      <IconButton onClick={handleDownloadHTML} disabled={loading}>
                        <HtmlIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box 
                  className="ql-snow"
                  sx={{ 
                    '& .ql-editor': { 
                      padding: '20px',
                      backgroundColor: '#fafafa',
                      borderRadius: 1,
                      border: 'none',
                      fontSize: '14px',
                      lineHeight: 1.6,
                    }
                  }}
                >
                  <div 
                    className="ql-editor" 
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} 
                  />
                </Box>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  color: 'text.secondary',
                  backgroundColor: 'grey.50',
                  borderRadius: 1
                }}
              >
                <PdfIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Sin convocatoria definida
                </Typography>
                <Typography variant="body2">
                  Haz clic en el botón de editar para agregar contenido
                </Typography>
              </Box>
            )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Diálogo de confirmación */}
      <Dialog open={openDialog} onClose={() => !loading && setOpenDialog(false)}>
        <DialogTitle>🗑️ Confirmar eliminación de PDF</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar el archivo PDF guardado? 
            Esta acción no se puede deshacer. El contenido de texto no se verá afectado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={confirmRemovePDF} 
            color="error" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ClearIcon />}
          >
            {loading ? 'Eliminando...' : 'Eliminar PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar mejorado */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

ConvocatoriaForm.propTypes = {
  competencia: PropTypes.object,
  updateConvocatoria: PropTypes.func.isRequired,
};

export default ConvocatoriaForm;
